<template>
  <main class="contacts-page">
    <section class="contacts-shell">
      <header class="contacts-header">
        <div>
          <p class="contacts-eyebrow">Directory</p>
          <h1>Contacts</h1>
        </div>

        <button class="new-contact-button" type="button" @click="openCreate">
          <v-icon size="18">mdi-account-plus-outline</v-icon>
          <span>New Contact</span>
        </button>
      </header>

      <div v-if="loadError" class="banner banner-error">{{ loadError }}</div>
      <div v-if="pageError" class="banner banner-error">{{ pageError }}</div>

      <div class="toolbar">
        <label class="search-field">
          <v-icon size="18">mdi-magnify</v-icon>
          <input v-model="searchTerm" type="text" placeholder="Search name, email, phone, service" />
        </label>

        <select v-model="statusFilter" class="status-filter">
          <option value="all">All statuses</option>
          <option v-for="(label, value) in statusLabels" :key="value" :value="value">{{ label }}</option>
        </select>
      </div>

      <div class="table-panel">
        <div v-if="loading" class="empty-list">
          <v-progress-circular indeterminate size="24" color="primary" />
          <span>Loading contacts…</span>
        </div>

        <table v-else-if="filteredContacts.length" class="contacts-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Leads</th>
              <th>Location</th>
              <th>Last source</th>
              <th class="actions-col"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="contact in filteredContacts"
              :key="contact.id"
              class="contact-row"
              @click="viewProfile(contact)"
            >
              <td>
                <div class="name-cell">
                  <strong>{{ contact.contact_name }}</strong>
                  <span
                    v-if="contact.lead_count"
                    class="status-pill"
                    :class="`status-${contact.last_lead_status}`"
                  >
                    {{ statusLabels[contact.last_lead_status] || contact.last_lead_status }}
                  </span>
                  <span v-else class="status-pill status-none">No leads yet</span>
                </div>
              </td>
              <td>{{ contact.email || '—' }}</td>
              <td>{{ contact.phone || '—' }}</td>
              <td>{{ contact.lead_count || 0 }}</td>
              <td>{{ locationLabel(contact) }}</td>
              <td>{{ sourceLabels[contact.last_lead_source] || contact.last_lead_source || '—' }}</td>
              <td class="actions-col">
                <button class="row-icon-button" type="button" title="Merge contact" @click.stop="openMerge(contact)">
                  <v-icon size="17">mdi-call-merge</v-icon>
                </button>
                <button
                  class="row-icon-button danger"
                  type="button"
                  title="Delete contact"
                  @click.stop="deleteContact(contact)"
                >
                  <v-icon size="17">mdi-trash-can-outline</v-icon>
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-else class="empty-list">
          <v-icon size="34">mdi-contacts-outline</v-icon>
          <h2>No contacts found</h2>
          <p>Try a different search or filter.</p>
        </div>
      </div>
    </section>

    <ContactFormModal
      v-model="showContactForm"
      :mode="contactFormMode"
      :contact="activeContact"
      :company-id="company.company_id"
      @saved="onContactSaved"
    />

    <MergeContactModal
      v-model="showMergeForm"
      :primary-contact="mergePrimaryContact"
      :contacts="contacts"
      :company-id="company.company_id"
      @merged="onContactMerged"
    />
  </main>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { company, getAuthToken, API_BASE_URL } from '@/stores/appConfig'
import { logAction } from '@/utils/logAction'
import ContactFormModal from '@/components/leads/ContactFormModal.vue'
import MergeContactModal from '@/components/leads/MergeContactModal.vue'
import { statusLabels, sourceLabels } from '@/constants/leads'

const route = useRoute()
const router = useRouter()

const contacts = ref([])
const loading = ref(false)
const loadError = ref('')
const pageError = ref('')

const searchTerm = ref('')
const statusFilter = ref(typeof route.query.status === 'string' ? route.query.status : 'all')

const showContactForm = ref(false)
const contactFormMode = ref('create')
const activeContact = ref(null)

const showMergeForm = ref(false)
const mergePrimaryContact = ref(null)

async function authHeaders() {
  const token = await getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function fetchContacts() {
  loading.value = true
  loadError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/contacts?company_id=${encodeURIComponent(company.company_id)}`,
      { headers }
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to load contacts.')
    }

    contacts.value = data.contacts || []
  } catch (err) {
    loadError.value = err.message || 'Failed to load contacts.'
  } finally {
    loading.value = false
  }
}

watchEffect(() => {
  if (company.company_id) fetchContacts()
})

const filteredContacts = computed(() => {
  const term = searchTerm.value.trim().toLowerCase()

  return contacts.value.filter((contact) => {
    if (statusFilter.value !== 'all' && contact.last_lead_status !== statusFilter.value) return false
    if (!term) return true

    return [contact.contact_name, contact.email, contact.phone, contact.last_lead_service]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(term))
  })
})

function locationLabel(contact) {
  return [contact.city, contact.province].filter(Boolean).join(', ') || '—'
}

function openCreate() {
  contactFormMode.value = 'create'
  activeContact.value = null
  showContactForm.value = true
}

function onContactSaved(contact) {
  if (contactFormMode.value === 'create') {
    contacts.value.unshift(contact)
  } else {
    const row = contacts.value.find((item) => item.id === contact.id)
    if (row) Object.assign(row, contact)
  }
}

function viewProfile(contact) {
  router.push(`/crm/customers/${contact.id}`)
}

function openMerge(contact) {
  mergePrimaryContact.value = contact
  showMergeForm.value = true
}

function onContactMerged({ keptContact, mergedId }) {
  contacts.value = contacts.value.filter((item) => item.id !== mergedId)
  const row = contacts.value.find((item) => item.id === keptContact.id)
  if (row) Object.assign(row, keptContact)
}

async function deleteContact(contact) {
  if (!confirm(`Delete ${contact.contact_name}? This removes the contact and all of their leads, messages, notes, and reminders — this can't be undone.`)) return

  pageError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/contacts/${contact.id}`, {
      method: 'DELETE',
      headers,
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to delete contact.')
    }

    contacts.value = contacts.value.filter((item) => item.id !== contact.id)

    const token = await getAuthToken()
    logAction(token, {
      action: 'contact.delete',
      label: `Deleted contact ${contact.contact_name}`,
      company_id: company.company_id,
      metadata: { contact_id: contact.id },
    })
  } catch (err) {
    pageError.value = err.message || 'Failed to delete contact.'
  }
}
</script>

<style scoped>
.contacts-page {
  min-height: calc(100dvh - 70px);
  padding: 28px 18px 104px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.contacts-shell {
  width: min(1440px, 100%);
  margin: 0 auto;
}

.contacts-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-end;
  margin-bottom: 18px;
}

.contacts-eyebrow {
  margin: 0 0 6px;
  color: #1f7a63;
  font-size: 0.76rem;
  font-weight: 850;
  text-transform: uppercase;
}

.contacts-header h1 {
  margin: 0;
  font-size: 2rem;
  line-height: 1.1;
  font-weight: 850;
}

.banner {
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.86rem;
}

.banner-error {
  color: #8a2b1f;
  background: #fdecea;
  border: 1px solid #f3c6bf;
}

.new-contact-button {
  height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  background: #1f7a63;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
}

.toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.search-field {
  height: 44px;
  flex: 1 1 320px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface);
}

.search-field input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  color: var(--text-primary);
  background: transparent;
  font-size: 0.92rem;
}

.status-filter {
  height: 44px;
  flex: 0 0 auto;
  padding: 0 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-primary);
  background: var(--surface);
  font: inherit;
  font-size: 0.88rem;
}

.table-panel {
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: 0 14px 34px rgba(23, 35, 31, 0.06);
  overflow: auto;
}

.contacts-table {
  width: 100%;
  border-collapse: collapse;
}

.contacts-table th {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-subtle);
  color: var(--text-secondary);
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
  text-align: left;
  white-space: nowrap;
}

.contacts-table td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-subtle);
  color: var(--text-primary);
  font-size: 0.88rem;
  vertical-align: middle;
}

.contact-row {
  cursor: pointer;
}

.contact-row:hover {
  background: var(--surface-alt);
}

.contact-row:last-child td {
  border-bottom: 0;
}

.name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.actions-col {
  width: 1%;
  white-space: nowrap;
  text-align: right;
}

.row-icon-button {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  margin-left: 6px;
  color: var(--text-secondary);
  background: var(--surface);
  cursor: pointer;
}

.row-icon-button:hover {
  color: #1f5c4d;
  border-color: #9fc8ba;
  background: #f3faf7;
}

.row-icon-button.danger:hover {
  color: #8a2b1f;
  border-color: #f3c6bf;
  background: #fdecea;
}

.status-pill {
  min-height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 750;
  white-space: nowrap;
}

.status-new {
  color: #8a4b0f;
  background: #fff1dc;
}

.status-contacted {
  color: #6a3fa0;
  background: #f2ebfb;
}

.status-quoted {
  color: #29528a;
  background: #e8f1ff;
}

.status-won {
  color: #1f5c4d;
  background: #e6f4ef;
}

.status-lost {
  color: var(--text-secondary);
  background: var(--surface-alt);
}

.status-none {
  color: var(--text-secondary);
  background: var(--surface-alt);
}

.empty-list {
  min-height: 220px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 8px;
  padding: 24px;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.empty-list h2 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.05rem;
}

.empty-list p {
  margin: 0;
}

@media (max-width: 900px) {
  .contacts-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .toolbar {
    flex-direction: column;
  }
}
</style>
